import React, { useState } from 'react';
import { HealthRecord, HealthProfile } from '../healthTypes';
import { calculateBMI, calculateAge, getBMIStatus, getStatusColor, getStatusLabel } from '../utils/healthUtils';
import { generateUUID } from '../utils/uuid';
import { X, Calendar, Ruler, Weight as WeightIcon } from 'lucide-react';

interface RecordFormProps {
  profile: HealthProfile;
  initialData?: HealthRecord;
  onSave: (record: HealthRecord) => void;
  onClose: () => void;
}

export const RecordForm: React.FC<RecordFormProps> = ({ profile, initialData, onSave, onClose }) => {
  const [date, setDate] = useState(initialData?.date || new Date().toISOString().split('T')[0]);
  const [weight, setWeight] = useState<string>(initialData?.weight.toString() || '');
  const [height, setHeight] = useState<string>(initialData?.height.toString() || profile.height?.toString() || '');
  const [note, setNote] = useState(initialData?.note || '');

  const numWeight = parseFloat(weight);
  const numHeight = parseFloat(height);
  const isValid = !isNaN(numWeight) && !isNaN(numHeight) && numWeight > 0 && numHeight > 0;

  const age = calculateAge(profile.birthDate);
  const previewBMI = isValid ? calculateBMI(numWeight, numHeight) : 0;
  const previewStatus = isValid ? getBMIStatus(previewBMI, age, profile.gender) : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    const newRecord: HealthRecord = {
      id: initialData?.id || generateUUID(),
      profileId: profile.id,
      date,
      height: numHeight,
      weight: numWeight,
      bmi: previewBMI,
      status: previewStatus!,
      note,
      timestamp: new Date(date).getTime(),
    };
    onSave(newRecord);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center pointer-events-none">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-auto transition-opacity" onClick={onClose} />

      <div className="bg-white dark:bg-slate-800 w-full max-w-md p-6 rounded-t-3xl sm:rounded-2xl shadow-2xl pointer-events-auto transform transition-transform animate-in slide-in-from-bottom duration-300">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              {initialData ? '编辑记录' : '记录一下'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {profile.name} · {age}岁
            </p>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-100 dark:bg-slate-700 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600">
            <X size={20} className="text-slate-600 dark:text-slate-300" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Calendar size={16} /> 日期
            </label>
            <input
              type="date"
              required
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full p-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none dark:text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Ruler size={16} /> 身高 (cm)
              </label>
              <input
                type="number"
                step="0.1"
                required
                placeholder="170"
                value={height}
                onChange={e => setHeight(e.target.value)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-lg font-medium dark:text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <WeightIcon size={16} /> 体重 (kg)
              </label>
              <input
                type="number"
                step="0.01"
                required
                placeholder="60.5"
                value={weight}
                onChange={e => setWeight(e.target.value)}
                className="w-full p-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-lg font-medium dark:text-white"
              />
            </div>
          </div>

          {isValid && previewStatus && (
            <div className={`p-4 rounded-xl border flex justify-between items-center ${getStatusColor(previewStatus)}`}>
              <div>
                <div className="text-xs opacity-80 uppercase tracking-wide">BMI 预览</div>
                <div className="text-2xl font-bold">{previewBMI}</div>
              </div>
              <div className="text-right">
                <div className="text-xs opacity-80">状态</div>
                <div className="font-bold">{getStatusLabel(previewStatus)}</div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">备注 (可选)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="今天吃得怎么样？"
              className="w-full p-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none h-20 resize-none text-sm dark:text-white"
            />
          </div>

          <button
            type="submit"
            disabled={!isValid}
            className="w-full py-4 bg-slate-900 dark:bg-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg hover:bg-slate-800 dark:hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            保存记录
          </button>
        </form>
      </div>
    </div>
  );
};
